import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback } from "react";
import { DeleteArticlesInput } from "../../../../__generated__/gql/graphql";
import { useToast } from "../../../../ui/Toast/ToastContext";
import CancelModal from "../../../../components/CancelModal/CancelModal";

const CancelArticlesGenerationMutation = graphql(/* GraphQL */ `
  mutation cancelArticlesGeneration($input: CancelArticlesGenerationInput!) {
    cancelArticlesGeneration(input: $input) {
      count
    }
  }
`);

const CancelArticlesGenerationModalQuery = graphql(/* GraphQL */ `
  query CancelArticlesGenerationModal($filters: [ArticleFilter!]) {
    articleCounts(input: { filters: $filters }) {
      total
      processing
      queued
    }
  }
`);

function InnerCancelArticlesGenerationModal({
  onClose,
  filters,
  isOpen,
  articleCounts,
}: {
  onClose: () => void;
  filters: DeleteArticlesInput["filters"];
  isOpen: boolean;
  articleCounts?: {
    total: number;
    processing: number;
    queued: number;
  };
}) {
  const { showToast } = useToast();
  const [{ fetching }, cancelArticlesGeneration] = useMutation(
    CancelArticlesGenerationMutation,
  );

  const onSubmit = useCallback(
    async ({
      cancelProcessing,
      cancelQueued,
    }: {
      cancelProcessing: boolean;
      cancelQueued: boolean;
    }) => {
      if (!cancelProcessing && !cancelQueued) {
        return onClose();
      }

      await cancelArticlesGeneration({
        input: {
          filters:
            !cancelProcessing || !cancelQueued
              ? filters?.map((filter) => {
                  return {
                    ...filter,
                    queued: !cancelProcessing && cancelQueued ? true : false,
                  };
                })
              : filters,
        },
      });

      if (articleCounts) {
        showToast({
          title: `Cancelled generation of ${articleCounts.processing} ${
            articleCounts.processing === 1 ? "article" : "articles"
          }`,
        });
      }
      onClose();
    },
    [articleCounts, cancelArticlesGeneration, filters, onClose, showToast],
  );

  return (
    <CancelModal
      onClose={onClose}
      fetching={fetching}
      onSubmit={onSubmit}
      articleCounts={articleCounts}
      isOpen={isOpen}
    />
  );
}

function CancelArticlesGenerationModal({
  isOpen,
  onClose,
  filters,
}: {
  isOpen: boolean;
  onClose: () => void;
  filters: DeleteArticlesInput["filters"];
}) {
  const [cancelArticlesGenerationModalResponse] = useQuery({
    query: CancelArticlesGenerationModalQuery,
    pause: !isOpen,
    variables: { filters },
  });

  const articleCounts =
    cancelArticlesGenerationModalResponse.data?.articleCounts;

  const title =
    articleCounts?.processing === 0
      ? `No articles in your selection are processing`
      : `Are you sure you want to cancel generation of ${articleCounts?.processing} ${
          articleCounts?.processing === 1 ? "article" : "articles"
        }?`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      loading={cancelArticlesGenerationModalResponse.fetching}
      title={title}
    >
      <InnerCancelArticlesGenerationModal
        onClose={onClose}
        filters={filters}
        isOpen={isOpen}
        articleCounts={articleCounts}
      />
    </Modal>
  );
}

export default CancelArticlesGenerationModal;
